import { useParams, useNavigate, useLocation } from "react-router-dom";
import { sanitizeCmsHtml } from "./rss";
import { useState, useEffect, useMemo } from "react";
import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";
import { StickyCTA } from "@/components/StickyCTA";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Calendar, Clock, Share2, ExternalLink, ArrowRight } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SEOHead } from "@/features/shared/SEOHead";
import { BLOG_SECTION, type BlogSection } from "@/features/blog/sections";

type FeedPost = {
  id: string;
  title: string;
  link: string;
  slug: string;
  description: string;
  content?: string;
  formattedDate: string;
  pubDate: string;
  category?: string;
  coverImage?: string;
  readTime?: string;
};

const calculateReadTime = (htmlContent: string | undefined) => {
  if (!htmlContent) return undefined;
  const div = document.createElement("div");
  div.innerHTML = htmlContent;
  const text = div.textContent || div.innerText || "";
  const wordsPerMinute = 200;
  const wordCount = text.trim().split(/\s+/).length;
  const minutes = Math.max(1, Math.ceil(wordCount / wordsPerMinute));
  return `${minutes} min read`;
};

const parseFeed = (raw: string): FeedPost[] => {
  const parser = new DOMParser();
  const xml = parser.parseFromString(raw, "application/xml");

  if (xml.querySelector("parsererror")) {
    throw new Error("Unable to parse RSS feed");
  }

  return Array.from(xml.querySelectorAll("item")).map((item, index) => {
    const title = item.querySelector("title")?.textContent?.trim() ?? "Untitled";
    const link = item.querySelector("link")?.textContent?.trim() ?? "";
    const description = item.querySelector("description")?.textContent ?? "";
    const content = sanitizeCmsHtml(item.getElementsByTagName("content:encoded")?.[0]?.textContent ?? undefined);
    const category = item.querySelector("category")?.textContent ?? undefined;
    const mediaContent = item.getElementsByTagName("media:content")?.[0]?.getAttribute("url") ||
      item.getElementsByTagName("media:content")?.[0]?.textContent ||
      undefined;
    const pubDate = item.querySelector("pubDate")?.textContent?.trim() ?? "";

    let slug = "";
    try {
      const url = new URL(link);
      const segments = url.pathname.split("/").filter(Boolean);
      slug = segments[segments.length - 1] ?? "";
    } catch (e) {
      slug = link.split("/").filter(Boolean).pop() ?? `${index}`;
    }

    const cleanedDescription = (() => {
      const div = document.createElement("div");
      div.innerHTML = description;
      return (div.textContent || div.innerText || "").trim();
    })();

    return {
      id: item.querySelector("guid")?.textContent?.trim() ?? `${index}-${title}`,
      title,
      link,
      slug,
      description: cleanedDescription,
      content,
      category,
      coverImage: mediaContent,
      formattedDate: pubDate
        ? new Date(pubDate).toLocaleDateString("en-CA", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
        : "",
      pubDate,
      readTime: calculateReadTime(content),
    };
  });
};

const BlogPostPage = ({ section = BLOG_SECTION }: { section?: BlogSection }) => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { post?: FeedPost } | null;
  const [post, setPost] = useState<FeedPost | null>(locationState?.post ?? null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) {
        setError("Post not found");
        setLoading(false);
        return;
      }

      if (locationState?.post) {
        setPost(locationState.post);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        let response = await fetch(section.localFeed);

        if (!response.ok) {
          console.warn("Local feed failed, falling back to external.");
          response = await fetch(section.externalFeed, {
            headers: {
              Accept: "application/rss+xml, application/xml;q=0.9, */*;q=0.8",
            },
          });
        }

        if (!response.ok) {
          throw new Error(`Feed returned ${response.status}`);
        }

        const raw = await response.text();
        const items = parseFeed(raw);
        const matched = items.find((item) => item.slug === slug);

        if (!matched) {
          setError("Post not found");
          return;
        }

        setPost(matched);
      } catch (err) {
        setError("Failed to load post");
      } finally {
        setLoading(false);
      }
    };

    void fetchPost();
  }, [locationState?.post, slug, section.localFeed, section.externalFeed]);

  const keywords = useMemo(() => {
    if (!post?.description) return "digital marketing, Thunder Bay, business growth";
    return post.description.split(" ").slice(0, 12).join(", ");
  }, [post?.description]);



  const handleShare = () => {
    if (navigator.share && post) {
      navigator.share({
        title: post.title,
        text: post.description,
        url: window.location.href,
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
    }
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-6 py-20">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-3/4"></div>
              <div className="h-4 bg-muted rounded w-1/2"></div>
              <div className="h-64 bg-muted rounded"></div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-6 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl font-bold mb-4">Post Not Found</h1>
            <p className="text-muted-foreground mb-8">
              The post you're looking for doesn't exist or has been moved.
            </p>
            <Button onClick={() => navigate(section.basePath)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to {section.label}
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const breadcrumbSchema = {
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://frayze.ca/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": section.label,
        "item": `https://frayze.ca${section.basePath}/`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": `https://frayze.ca${section.basePath}/${slug}/`
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <SEOHead
        title={post ? `${post.title} | Frayze ${section.label}` : `${section.label} | Frayze`}
        description={post?.description || "Read the latest insights from Frayze's digital marketing experts."}
        keywords={keywords}
        ogTitle={post?.title || "Blog Post"}
        ogDescription={post?.description || "Read the latest insights from Frayze's digital marketing experts."}
        ogImage={post?.coverImage}
        twitterTitle={post?.title || "Blog Post"}
        twitterDescription={post?.description || "Read the latest insights from Frayze's digital marketing experts."}
        twitterImage={post?.coverImage}
        canonical={`https://frayze.ca${section.basePath}/${slug}/`}
        robots="index, follow, max-snippet:-1, max-image-preview:large"
        structuredData={breadcrumbSchema}
      />

      <article className="py-20">
        <div className="container mx-auto px-6 py-4 mb-8">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href={`${section.basePath}/`}>{section.label}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="line-clamp-1 max-w-[200px] md:max-w-xs text-gray-900/80">{post.title}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {/* Back Button */}
            <Button
              variant="ghost"
              className="mb-8"
              onClick={() => navigate(section.basePath)}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to {section.label}
            </Button>

            {/* Post Header */}
            <header className="mb-8">
              {post.coverImage && (
                <img
                  src={post.coverImage}
                  alt={post.title}
                  className="w-full aspect-[1200/630] object-cover object-center rounded-lg mb-6 bg-slate-900"
                />
              )}

              {post.category && (
                <div className="flex items-center gap-2 mb-4">
                  <Badge variant="secondary">{post.category}</Badge>
                </div>
              )}

              <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                {post.title}
              </h1>

              <div className="flex items-center justify-between text-sm text-muted-foreground mb-6">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {post.formattedDate}
                  </span>
                  {post.readTime && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {post.readTime}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handleShare}>
                    <Share2 className="h-4 w-4 mr-1" />
                    Share
                  </Button>

                </div>
              </div>
            </header>

            {/* Post Content: demote any body <h1> to <h2> so the post title is the only H1 (SEO) */}
            <div
              className="prose prose-lg max-w-none prose-headings:font-bold prose-a:text-primary prose-a:no-underline hover:prose-a:underline"
              dangerouslySetInnerHTML={{ __html: (post.content ?? "").replace(/<(\/?)h1\b/gi, "<$1h2") }}
            />

            {/* Call to Action */}
            <div className="mt-12 p-8 bg-gradient-hero rounded-2xl text-center shadow-xl">
              <h3 className="text-3xl font-bold text-white mb-3">
                Stop losing jobs to slow follow-up.
              </h3>
              <p className="text-xl text-white/90 mb-6 max-w-2xl mx-auto">
                We build the system that texts back missed calls, follows up automatically, and books the job, so Thunder Bay business owners stay focused on the work, not the phone.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  variant="cta"
                  className="font-bold text-lg px-8 py-6 shadow-lg"
                  onClick={() => navigate('/website-launch-program/')}
                >
                  See How It Works
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 border-white/30 text-white hover:bg-white hover:text-primary font-bold text-lg px-8 py-6"
                  onClick={() => navigate('/book-consultation/')}
                >
                  Book a Free 15-Min Call
                </Button>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
      <StickyCTA />
    </div>
  );
};

export default BlogPostPage;
